import React from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { useT, useLocale } from '../../i18n/LocaleContext';
import { formatCurrency, formatDate } from '../../utils/format';
import { RESERVATION_STATUS_TONE } from './constants';

function formatDateTime(value, locale) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(locale === 'fr' ? 'fr-RW' : 'en-RW', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function Detail({ label, children, mono }) {
  return (
    <div>
      <div className="detail-item-label">{label}</div>
      <div className={mono ? 'detail-item-value mono-value' : 'detail-item-value'}>{children || '—'}</div>
    </div>
  );
}

export default function ReservationDetailModal({
  isOpen,
  onClose,
  reservation,
  canUpdate,
  onRecheck,
  onCancel,
  rechecking,
}) {
  const t = useT();
  const { locale } = useLocale();

  if (!reservation) return null;
  const r = reservation;
  const isConfirmed = r.status === 'confirmed';
  const isCancelled = r.status === 'cancelled';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('reservations_page.view_title')}
      subtitle={r.eventName}
      size="lg"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {canUpdate && !isConfirmed && !isCancelled && (
            <Button variant="danger" onClick={onCancel}>{t('reservations_page.cancel_reservation')}</Button>
          )}
          {canUpdate && !isCancelled && (
            <Button variant="primary" onClick={onRecheck} loading={rechecking}>{t('reservations_page.recheck')}</Button>
          )}
        </>
      )}
    >
      <div className="detail-hero">
        <div>
          <div className="mono" style={{ fontSize: '13px' }}>{r.code}</div>
          <div className="detail-hero__amount">{formatCurrency(r.totalAmount, locale)}</div>
          <div className="form-hint" style={{ marginTop: 2 }}>
            {t('reservations_page.amount_breakdown', {
              people: r.peopleCount,
              unit: formatCurrency(r.unitPrice, locale),
              fee: formatCurrency(r.feePerPerson, locale),
            })}
          </div>
        </div>
        <Badge tone={RESERVATION_STATUS_TONE[r.status]}>{t(`reservations_page.statuses.${r.status}`)}</Badge>
      </div>

      <div className="detail-section-title">{t('reservations_page.section_contact')}</div>
      <div className="detail-grid">
        <Detail label={t('common.full_name')}>{`${r.contactFirstName || ''} ${r.contactLastName || ''}`.trim()}</Detail>
        <Detail label={t('common.phone')}>{r.contactPhone}</Detail>
        <Detail label={t('common.email')}>{r.contactEmail}</Detail>
        <Detail label={t('reservations_page.field_parish')}>{r.contactParish}</Detail>
        <Detail label={t('reservations_page.field_emergency')}>{r.emergencyPhone}</Detail>
        <Detail label={t('reservations_page.field_pilgrimage')}>
          {r.eventName}{r.eventDate ? ` · ${formatDate(r.eventDate, t)}` : ''}
        </Detail>
      </div>

      <div className="detail-section-title">
        {t('reservations_page.section_participants', { count: r.participants.length })}
      </div>
      <div className="table-wrap detail-table">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>{t('common.full_name')}</th>
              <th>{t('common.phone')}</th>
              <th>{t('reservations_page.field_parish')}</th>
              <th>{t('members.field_gender')}</th>
              <th>{t('reservations_page.field_pickup')}</th>
              <th>{t('reservations_page.field_member')}</th>
            </tr>
          </thead>
          <tbody>
            {r.participants.map((p) => (
              <tr key={p.position}>
                <td className="mono">{p.position}</td>
                <td><strong>{p.firstName} {p.lastName}</strong></td>
                <td>{p.phone || '—'}</td>
                <td>{p.parish || '—'}</td>
                <td>{p.gender ? t(`members.genders.${p.gender}`) : '—'}</td>
                <td>{p.departureLocationName || '—'}</td>
                <td>{p.communityMember ? <Badge tone="active">{t('reservations_page.member_yes')}</Badge> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="detail-section-title">{t('reservations_page.section_payment')}</div>
      <div className="detail-grid">
        <Detail label={t('reservations_page.field_method')}>{t(`reservations_page.methods.${r.payMethod}`)}</Detail>
        <Detail label={t('reservations_page.field_payer')} mono>{r.payerMsisdn}</Detail>
        <Detail label={t('reservations_page.field_customer_ref')} mono>{r.customerRef}</Detail>
        <Detail label={t('reservations_page.field_gateway_ref')} mono>{r.gatewayRef}</Detail>
        <Detail label={t('reservations_page.field_gateway_tid')} mono>{r.gatewayTid}</Detail>
        <Detail label={t('reservations_page.field_gateway_status')}>{r.gatewayStatus}</Detail>
        <Detail label={t('reservations_page.field_linked_payment')} mono>{r.paymentCode}</Detail>
        <Detail label={t('reservations_page.field_paid_at')}>{r.paidAt ? formatDateTime(r.paidAt, locale) : null}</Detail>
        <Detail label={t('reservations_page.field_created_at')}>{formatDateTime(r.createdAt, locale)}</Detail>
      </div>
      {r.gatewayMessage && (
        <div className="detail-note">
          <div className="detail-item-label">{t('reservations_page.field_gateway_message')}</div>
          <div className="detail-item-value">{r.gatewayMessage}</div>
        </div>
      )}
    </Modal>
  );
}
